import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { Trash2Icon } from "lucide-react";

import { usePlanningPolicies, type PlanningPolicy } from "@/api/planningPolicy";
import { DataTable, type ColumnDef } from "@/components/data-table/DataTable";
import { useDataTableState } from "@/components/data-table/useDataTableState";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { formatPercent, formatRelativeTime } from "@/utils/formatters";

import { DeletePolicyDialog } from "./DeletePolicyDialog";
import { PolicyDialog } from "./PolicyDialog";

export default function PlanningPoliciesPage() {
  const { t } = useTranslation();

  const ds = useDataTableState({
    defaultSort: { id: "name", desc: false },
    defaultPageSize: 50,
    filterDeps: [],
  });

  const query = usePlanningPolicies({
    search: ds.debouncedSearch || undefined,
    ordering: ds.orderingParam,
  });

  const [dialog, setDialog] = useState<PlanningPolicy | "new" | null>(null);
  const [deleting, setDeleting] = useState<PlanningPolicy | null>(null);

  const columns = useMemo<ColumnDef<PlanningPolicy>[]>(
    () => [
      {
        accessorKey: "name",
        header: t("planningPolicies.columnTitleName"),
        enableSorting: true,
      },
      {
        id: "strategy",
        header: t("planningPolicies.columnTitleStrategy"),
        size: 160,
        cell: ({ row }) => (
          <span className="text-muted-foreground text-xs">
            {row.original.regime === "min_max"
              ? t("planningPolicies.strategy.minMax")
              : t("planningPolicies.strategy.serviceLevel", {
                  pct: formatPercent(row.original.serviceLevel ?? 0),
                })}
          </span>
        ),
      },
      {
        id: "lotSizing",
        header: t("planningPolicies.columnTitleLotSizing"),
        size: 160,
        cell: ({ row }) => {
          const p = row.original;
          const label =
            p.lotSizingMethod === "fixed_period"
              ? t("planningPolicies.lotSizing.fixedPeriodCovers", {
                  days: p.orderCoverageDays,
                })
              : p.lotSizingMethod === "lead_time_coverage"
                ? t("planningPolicies.lotSizing.leadTimeCoverage")
                : null;
          return <span className="text-muted-foreground text-xs">{label}</span>;
        },
      },
      {
        accessorKey: "memberCount",
        header: t("planningPolicies.columnTitleMembers"),
        size: 100,
        enableSorting: true,
      },
      {
        accessorKey: "updatedAt",
        header: t("planningPolicies.columnTitleUpdatedAt"),
        cell: ({ row }) =>
          row.original.updatedAt
            ? formatRelativeTime(row.original.updatedAt)
            : "—",
        size: 160,
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        size: 48,
        cell: ({ row }) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="icon-sm"
              data-testid="delete"
              aria-label={t("common.delete")}
              onClick={() => setDeleting(row.original)}
            >
              <Trash2Icon className="text-muted-foreground size-4" />
            </Button>
          </div>
        ),
      },
    ],
    [t],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("planningPolicies.title")}
        actions={
          <Button onClick={() => setDialog("new")}>
            {t("planningPolicies.new")}
          </Button>
        }
      />

      <DataTable<PlanningPolicy>
        {...ds.tableProps}
        data={query.data ?? []}
        totalCount={query.data?.length ?? 0}
        isLoading={query.isLoading}
        columns={columns}
        searchPlaceholder={t("planningPolicies.searchPlaceholder")}
        emptyLabel={t("planningPolicies.empty")}
        getRowId={(r) => String(r.id)}
        onRowClick={(row) => setDialog(row)}
      />

      {dialog !== null && (
        <PolicyDialog
          policy={dialog === "new" ? null : dialog}
          onClose={() => setDialog(null)}
        />
      )}
      <DeletePolicyDialog policy={deleting} onClose={() => setDeleting(null)} />
    </div>
  );
}
